import { landmineNames } from '@mm/constants/landmine-names';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { INewBillingModal2023Props } from '@mm/state/modules/modalsState/props';
import { selectActiveEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import {
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectInstagramAccountId } from '@mm/state/selectors/instagramSelectors';
import {
  hasSMSNumberSelector,
  recentDiscardedSelector,
  selectIsSmsSupported,
} from '@mm/state/selectors/twillio';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { assertUnreachable } from '@mm/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useConnectPageHandlers } from '../../../../../../../common/hooks/use-connect-page-handlers';
import { useFreeTrialUpgradeModal } from '../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { useSetNextOnboardingStep } from '../../../../../../../dashboard/pages/SignupOnboarding/use-set-next-onboarding-step';
import { IG_AUTOMATIONS_SIGNUP_GA_EVENT } from '../../AutomationHome/TemplateIndex/TemplateGoButton';

export const CONNECTABLE_CHANNELS = ['email', 'facebook', 'instagram'] as const;
export type ConnectableChannel = typeof CONNECTABLE_CHANNELS[number];

export const useChannelConnection = (
  onChannelConnected: () => void,
  botId?: number,
  isOnboarding?: boolean,
  otherFacebookPage?: EnhancedBillingPage,
  billingModalProps?: INewBillingModal2023Props,
) => {
  const isFacebookConnectedOnActivePage = useSelector(
    hasConnectedRealFacebookPageSelector,
  );
  const isFacebookConnected = otherFacebookPage
    ? !!otherFacebookPage.realFacebookPage
    : isFacebookConnectedOnActivePage;
  const isInstagramConnected = !!useSelector(selectInstagramAccountId);
  const hasInstachampCompanyFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).instachamp;
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;

  const isSMSSupported = useSelector(selectIsSmsSupported);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const hasSMSNumber = useSelector(hasSMSNumberSelector);
  const isSMSConnected = activeFacebookPage?.smsNumberActivated || hasSMSNumber;
  const isEmailConnected =
    !!useSelector(selectActiveEmailAccounts)?.length ||
    !!activeFacebookPage?.emailActivated;
  const enhancedBillingPages = useSelector(enhancedBillingPagesSelector);
  const facebookPage = otherFacebookPage
    ? otherFacebookPage
    : activeFacebookPage
    ? activeFacebookPage
    : enhancedBillingPages[0];

  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal(facebookPage);
  const recentDiscardedNumber = useSelector(recentDiscardedSelector);

  const { openInstagramConnectModal } = useModalActions();
  const connectIgOnSuccess = useRef(false);
  const { attachToFacebookPage } = useChatbotCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const { openAudienceExtractorModal } = useModalActions();
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();
  const enabledInputTriggers = useSelector(
    (state: StoreState) => state.campaignsOverview.enabledInputTriggers,
  );

  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const retrieveEnableInputTriggers = useCallback(() => {
    if (botId) {
      campaignsOverviewCRUDActions.retrieveEnabledInputTriggers(botId);
    }
  }, [botId, campaignsOverviewCRUDActions]);

  useEffect(() => {
    retrieveEnableInputTriggers();
  }, [retrieveEnableInputTriggers]);

  const onSuccessConnect = useCallback(
    channel => {
      if (
        !enabledInputTriggers.find(
          trigger =>
            trigger ===
            (channel === 'instagram'
              ? 'instagram_catch_all'
              : 'facebook_catch_all'),
        )
      ) {
        if (isOnboarding) {
          onChannelConnected();
        } else if (!hasInstachampCompanyFlag) {
          openAudienceExtractorModal({ channel });
        }
      } else {
        onChannelConnected();
      }
    },
    [
      openAudienceExtractorModal,
      onChannelConnected,
      enabledInputTriggers,
      isOnboarding,
      hasInstachampCompanyFlag,
    ],
  );

  const { connect } = useConnectPageHandlers({
    otherFacebookPage: facebookPage,
    onSuccess: () => {
      if (!isFacebookConnected && connectIgOnSuccess.current) {
        openInstagramConnectModal({
          showCongratulations: true,
          eventName: IG_AUTOMATIONS_SIGNUP_GA_EVENT,
          onSuccessInstagramConnect: () => onSuccessConnect('instagram'),
          otherFacebookPage: facebookPage,
        });
        return;
      }
      onSuccessConnect('facebook');
    },
  });

  const { getGmailOauthUrl } = useEmailAccountCRUDActions();
  const connectInstagramAccount = useMemo(
    () => async () => {
      if (!isFacebookConnected) {
        connectIgOnSuccess.current = true;
        await connect();
      } else {
        openInstagramConnectModal({
          showCongratulations: true,
          onSuccessInstagramConnect: () => onSuccessConnect('instagram'),
          otherFacebookPage: facebookPage,
        });
      }
    },
    [
      connect,
      isFacebookConnected,
      openInstagramConnectModal,
      onSuccessConnect,
      facebookPage,
    ],
  );

  const onConnectGmail = useMemo(
    () => async () => {
      if (isOnboarding && botId) {
        await setNextOnboardingStep(OnboardingSignupStep.NONE, false);
      }
      if (!facebookPage?.activeBotId) {
        await attachToFacebookPage(Number(botId), facebookPage.id, true);
      }
      if (!hasNativeIntegrationsFeature && !isFreeTrialPromotionActive) {
        openFreeTrialUpgradeModalPromotional({
          ...billingModalProps,
          landmineName:
            billingModalProps?.landmineName ||
            landmineNames.CONNECT_GMAIL_ACCOUNT,
        });
        return;
      }
      window.location.href = await getGmailOauthUrl(facebookPage.id);
    },
    [
      getGmailOauthUrl,
      facebookPage,
      setNextOnboardingStep,
      attachToFacebookPage,
      botId,
      isOnboarding,
      hasNativeIntegrationsFeature,
      openFreeTrialUpgradeModalPromotional,
      isFreeTrialPromotionActive,
      billingModalProps,
    ],
  );
  const connectChannel = useMemo(
    () => (channel: ConnectableChannel) => {
      switch (channel) {
        case 'facebook':
          connect();
          break;
        case 'instagram':
          connectInstagramAccount();
          break;
        case 'email':
          onConnectGmail();
          break;
        default:
          assertUnreachable(channel);
      }
    },
    [connect, connectInstagramAccount, onConnectGmail],
  );

  return {
    connectChannel,
    isFacebookConnected,
    isInstagramConnected,
    isSMSConnected,
    isEmailConnected,
    isSMSSupported,
    recentDiscardedNumber,
  };
};
